import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { KeyRound, Loader2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { Field, FormAlert } from '@/components/app/form'
import { Button } from '@/components/ui/button'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { safeRedirect } from '@/lib/session'

/**
 * Second step of a sign-in for accounts with an authenticator app. The
 * password step left a short-lived `two_factor` cookie behind; the code (or
 * a backup code) turns it into a session. Ten minutes, then start over.
 */
export const Route = createFileRoute('/_auth/two-factor')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  head: () => ({ meta: [{ title: 'Two-factor code · Nebueye' }] }),
  component: TwoFactor,
})

function TwoFactor() {
  const search = Route.useSearch()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [backup, setBackup] = useState(false)
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const code = String(form.get('code')).replace(/\s+/g, '')
    const trustDevice = form.get('trust') === 'on'
    setPending(true)
    setError(undefined)
    const { error } = backup
      ? await authClient.twoFactor.verifyBackupCode({ code, trustDevice })
      : await authClient.twoFactor.verifyTotp({ code, trustDevice })
    setPending(false)
    if (error) return setError(authErrorMessage(error))
    await queryClient.invalidateQueries()
    router.history.push(safeRedirect(search.redirect))
  }

  return (
    <div className="space-y-7">
      <span className="bg-accent text-accent-foreground grid size-12 place-items-center rounded-xl">
        <KeyRound className="size-6" />
      </span>
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">{backup ? 'Use a backup code' : 'Enter your code'}</h1>
        <p className="text-muted-foreground text-[15px]">
          {backup ? 'One of the codes you saved when you set up two-factor. Each works once.' : 'Open your authenticator app and enter the 6-digit code for Nebueye.'}
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <FormAlert>{error}</FormAlert>}
        <Field
          key={backup ? 'backup' : 'totp'}
          label={backup ? 'Backup code' : 'Authenticator code'}
          name="code"
          inputMode={backup ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          className="font-mono"
          required
          autoFocus
        />
        <label className="flex items-center gap-2.5 text-sm">
          <input type="checkbox" name="trust" className="accent-primary size-4" />
          Trust this device for 30 days
        </label>
        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending && <Loader2 className="animate-spin" />}
          Continue
        </Button>
      </form>

      <div className="text-muted-foreground space-y-2 text-sm">
        <button type="button" className="text-primary font-medium hover:underline" onClick={() => { setBackup((b) => !b); setError(undefined) }}>
          {backup ? 'Use the authenticator app instead' : "Can't reach your app? Use a backup code"}
        </button>
        <p>
          <Link to="/sign-in" search={{ redirect: search.redirect }} className="hover:text-foreground">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
